const STORAGE_KEY = 'fanuc.attribution';
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

const STORAGE_VERSION = 1;

function normalizeHandle(value) {
  return String(value || '')
    .replace(/^fields\[([^\]]+)]$/, '$1')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
}

function camelUtm(param) {
  return param.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
}

function readStoredAttribution(localStorage) {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return stored?.version === STORAGE_VERSION ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredAttribution(localStorage, state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: STORAGE_VERSION,
        first: state.first,
        last: state.last,
        landing_page_url: state.landing_page_url,
        referrer_url: state.referrer_url,
      }),
    );
  } catch {
    // Storage can be blocked by privacy settings.
  }
}

function getUtmParams(url) {
  const params = {};

  for (const key of UTM_PARAMS) {
    const value = url.searchParams.get(key);
    if (value) params[key] = value;
  }

  return params;
}

function hasValues(value) {
  return Object.keys(value || {}).length > 0;
}

export function buildAttributionState({ href, referrer = '', localStorage }) {
  const url = new URL(href);
  const stored = localStorage ? readStoredAttribution(localStorage) : null;
  const currentUtms = getUtmParams(url);
  const hasCurrentUtms = hasValues(currentUtms);

  const state = {
    first: stored?.first || (hasCurrentUtms ? currentUtms : {}),
    last: hasCurrentUtms ? currentUtms : stored?.last || {},
    landing_page_url: stored?.landing_page_url || href,
    current_page_url: href,
    referrer_url: stored?.referrer_url || referrer || '',
  };

  if (localStorage) writeStoredAttribution(localStorage, state);

  return state;
}

function buildFieldMap(state) {
  const map = new Map();

  for (const param of UTM_PARAMS) {
    const camel = camelUtm(param);
    const firstValue = state.first?.[param] || '';
    const lastValue = state.last?.[param] || '';

    map.set(normalizeHandle(param), lastValue);
    map.set(normalizeHandle(camel), lastValue);
    map.set(normalizeHandle(`first_${param}`), firstValue);
    map.set(normalizeHandle(`first_${camel}`), firstValue);
    map.set(
      normalizeHandle(`first${camel[0].toUpperCase()}${camel.slice(1)}`),
      firstValue,
    );
    map.set(normalizeHandle(`last_${param}`), lastValue);
    map.set(normalizeHandle(`last_${camel}`), lastValue);
    map.set(
      normalizeHandle(`last${camel[0].toUpperCase()}${camel.slice(1)}`),
      lastValue,
    );
  }

  map.set(normalizeHandle('landingPageUrl'), state.landing_page_url || '');
  map.set(normalizeHandle('landing_page_url'), state.landing_page_url || '');
  map.set(normalizeHandle('currentPageUrl'), state.current_page_url || '');
  map.set(normalizeHandle('current_page_url'), state.current_page_url || '');
  map.set(normalizeHandle('referrerUrl'), state.referrer_url || '');
  map.set(normalizeHandle('referrer_url'), state.referrer_url || '');
  map.set(normalizeHandle('submissionUrl'), state.current_page_url || '');
  map.set(normalizeHandle('httpReferUrl'), state.referrer_url || '');

  return map;
}

export function populateAttributionFields(form, state) {
  if (!form) return;

  const fieldMap = buildFieldMap(state);
  const inputs = form.querySelectorAll('input[name]');

  for (const input of inputs) {
    const value = fieldMap.get(normalizeHandle(input.name));
    if (!value) continue;

    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function populateAllForms(document, state) {
  for (const form of document.querySelectorAll('form')) {
    populateAttributionFields(form, state);
  }
}

export function initAttribution(windowObj = globalThis.window) {
  if (!windowObj) return null;

  const { document, localStorage, location } = windowObj;
  if (!document || !location) return null;

  const state = buildAttributionState({
    href: location.href,
    referrer: document.referrer,
    localStorage,
  });

  const populate = () => populateAllForms(document, state);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', populate, { once: true });
  } else {
    populate();
  }

  document.addEventListener('onFormieLoaded', populate);
  document.addEventListener('onAfterFormieInit', populate);
  document.addEventListener(
    'submit',
    (event) => populateAttributionFields(event.target, state),
    true,
  );
  windowObj.addEventListener?.('modal:open', () =>
    windowObj.setTimeout(populate, 0),
  );

  return state;
}

if (typeof window !== 'undefined') {
  initAttribution(window);
}
